import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom'
import { clearToastOnRouteChange } from '@/shared/ui/toast'
import { TabLayout, PlainLayout } from '@/shared/components/app-shell'
import { ProtectedRoute } from '@/features/auth/protected-route'
import { AdminRoute } from '@/features/auth/admin-route'
import { ErrorBoundary } from '@/shared/components/error-boundary'

// ── EAGER ────────────────────────────────────────────────────────────────────
// The screens on a first-session path: sign in → onboard → feed → a match →
// create. Plus the share door (`/match/:id`), which is the app's single most
// important first impression — a WhatsApp invitee must not wait on a chunk.
import { LoginPage } from '@/features/auth/login-page'
import { ForgotPasswordPage } from '@/features/auth/forgot-password-page'
import { OnboardingPage } from '@/features/auth/onboarding-page'
import { PartidosPage } from '@/features/partidos/partidos-page'
import { MatchDetailPage } from '@/features/partidos/match-detail-page'
import { MatchPreviewPage } from '@/features/partidos/match-preview-page'
import { LugaresPage } from '@/features/lugares/lugares-page'
import { MisPartidosPage } from '@/features/mis-partidos/mis-partidos-page'
import { PerfilPage } from '@/features/perfil/perfil-page'
import { CrearPartidoPage } from '@/features/crear/crear-partido-page'

// ── LAZY ─────────────────────────────────────────────────────────────────────
// Everything a player reaches only on purpose, and the admin console no player
// reaches at all. Before this the whole app was one chunk: the 3-tab admin
// console plus the legal pages plus the mock fixtures shipped to every player
// on first paint (verified — the string "Moderación" was in the player bundle).
const AdminPage = lazy(() =>
  import('@/features/admin/admin-page').then((m) => ({ default: m.AdminPage })),
)
const PrivacidadPage = lazy(() =>
  import('@/features/legal/legal-pages').then((m) => ({ default: m.PrivacidadPage })),
)
const TerminosPage = lazy(() =>
  import('@/features/legal/legal-pages').then((m) => ({ default: m.TerminosPage })),
)
const ClubInvitePage = lazy(() =>
  import('@/features/lugares/club-invite-page').then((m) => ({ default: m.ClubInvitePage })),
)
const ClubDetailPage = lazy(() =>
  import('@/features/lugares/club-detail-page').then((m) => ({ default: m.ClubDetailPage })),
)
const EditarPerfilPage = lazy(() =>
  import('@/features/perfil/editar-perfil-page').then((m) => ({ default: m.EditarPerfilPage })),
)
const EditarPartidoPage = lazy(() =>
  import('@/features/crear/editar-partido-page').then((m) => ({ default: m.EditarPartidoPage })),
)
const ContactosPage = lazy(() =>
  import('@/features/contactos/contactos-page').then((m) => ({ default: m.ContactosPage })),
)
const AjustesPage = lazy(() =>
  import('@/features/ajustes/ajustes-page').then((m) => ({ default: m.AjustesPage })),
)
const ActividadPage = lazy(() =>
  import('@/features/actividad/actividad-page').then((m) => ({ default: m.ActividadPage })),
)

/**
 * Chunk-load boundary. `aria-busy` with no visible spinner on purpose: on any
 * real connection these chunks resolve inside a frame or two, and a spinner
 * that flashes for 40ms reads as jank. Screen readers still get the state.
 *
 * Also wraps each lazy route in an ErrorBoundary. The app-wide boundary in
 * `main.tsx` catches everything, but it replaces the WHOLE app — so one screen
 * throwing takes the nav bar with it and the player's only way out is a reload
 * (into the same precached shell). Per-route isolation keeps the failure the
 * size of the failure: the broken screen offers a retry, the rest still works.
 * A chunk that fails to LOAD (offline, stale hash after a deploy) surfaces here
 * too, which is the common case rather than the exotic one.
 */
function Lazy({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<div className="flex-1" aria-busy />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

// Root shell: clears stale toasts on navigation (a confirmation fired FOR the
// navigation survives; older ones must not bleed onto the next screen).
function RootLayout() {
  const location = useLocation()
  useEffect(() => {
    clearToastOnRouteChange()
  }, [location.pathname])
  // Splash failsafe — retire the stuck-splash class (3rd occurrence
  // 2026-07-05: legal pages ×2, then forgot-password). Pages lift the boot
  // splash early via useAppReady(); if a future bare route forgets, this
  // guarantees the splash NEVER outlives 4s. Real lifts happen well before.
  useEffect(() => {
    const t = window.setTimeout(() => document.body.classList.add('app-ready'), 4000)
    return () => window.clearTimeout(t)
  }, [])
  return <Outlet />
}

export const router = createBrowserRouter([
  {
  element: <RootLayout />,
  children: [
  // Bare auth screens (no shell)
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/onboarding', element: <OnboardingPage /> },

  // PUBLIC pages — deliberately outside ProtectedRoute:
  // shared-link preview (docs/match-sharing.md) + club join-link (E4) +
  // legal (a visitor reads them BEFORE creating an account).
  { path: '/match/:id', element: <MatchPreviewPage /> },
  { path: '/unete/:id', element: <Lazy><ClubInvitePage /></Lazy> },
  { path: '/privacidad', element: <Lazy><PrivacidadPage /></Lazy> },
  { path: '/terminos', element: <Lazy><TerminosPage /></Lazy> },

  // Top-level destinations — bottom tab bar
  {
    element: (
      <ProtectedRoute>
        <TabLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: '/', element: <Navigate to="/partidos" replace /> },
      { path: '/partidos', element: <PartidosPage /> },
      { path: '/lugares', element: <LugaresPage /> },
      { path: '/mis-partidos', element: <MisPartidosPage /> },
      { path: '/perfil', element: <PerfilPage /> },
    ],
  },

  // Full-screen pushed screens — no tab bar, back affordance
  {
    element: (
      <ProtectedRoute>
        <PlainLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: '/crear', element: <CrearPartidoPage /> },
      { path: '/editar-partido/:id', element: <Lazy><EditarPartidoPage /></Lazy> },
      { path: '/partido/:id', element: <MatchDetailPage /> },
      { path: '/club/:id', element: <Lazy><ClubDetailPage /></Lazy> },
      { path: '/contactos', element: <Lazy><ContactosPage /></Lazy> },
      { path: '/ajustes', element: <Lazy><AjustesPage /></Lazy> },
      { path: '/actividad', element: <Lazy><ActividadPage /></Lazy> },
      { path: '/editar-perfil', element: <Lazy><EditarPerfilPage /></Lazy> },
      {
        path: '/admin',
        element: (
          <AdminRoute>
            <Lazy>
              <AdminPage />
            </Lazy>
          </AdminRoute>
        ),
      },
    ],
  },

  { path: '*', element: <Navigate to="/" replace /> },
  ],
  },
])
